import _ from 'lodash';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {ScrollDirectionsEnum} from '../../../../../../srcCommon/scroll/scrollerTypes';
import {CancelToken} from '../../../../../helpers/asyncCancelHelpers';
import {findConversationsCanceledReminder} from '../../../../../helpers/models/conversationHelpers';
import {
  makeMapStateToProps,
  wrapInArrayToSetSelector,
  wrapInShallowSelector,
} from '../../../../../helpers/reselectHelpers';
import {ConversationTimelineBoundary} from '../../../../../helpers/viewerHelpers';
import {ActivityVisualTypesEnum} from '../../../../../models/api/activityModel';
import {ChannelModel} from '../../../../../models/api/channelModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {DraftMessageModel, MessageModel} from '../../../../../models/api/messageModel';
import {AppState} from '../../../../../store/appState';
import {Dispatch} from '../../../../../store/appStore';
import {makeGetReplyChannel} from '../../../../../store/data/channels/channelsSelectors';
import {makeGetPrecogs} from '../../../../../store/data/conversations/conversationsPrecogsSelectors';
import {
  makeGetConversationIsSpam,
  makeGetFetchedAt,
  makeGetLoadedDraftMessages,
  makeGetUserDraftComposeMessages,
} from '../../../../../store/data/conversations/conversationsSelectors';
import {getChatPrecogs, getComments, getMessages} from '../../../../../store/data/dataSelectors';
import {getInboxes} from '../../../../../store/data/inboxes/inboxesSelectors';
import {deleteEmptyDraftsAction} from '../../../../../store/data/messages/draftMessagesActions';
import {OptionalMessageSelector} from '../../../../../store/data/messages/messagesSelectors';
import {getFindTeammate} from '../../../../../store/data/teammates/lazyTeammatesSelectors';
import {getUserAlias, getUserId} from '../../../../../store/data/teammates/teammatesSelectors';
import {
  getHasAutoShareDrafts,
  getHasListSendingMessagesInSentFolder,
  getHasRecentCommunicationsBanner,
  getHasRecentCommunicationsComposerBanner,
} from '../../../../../store/featureFlags/featureFlagsSelectors';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {fetchMoreConversationAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {
  ActivitiesSelector,
  makeGetCommentingTeammates,
  makeGetDraftSeedAuthors,
  makeGetPageActivities,
  makeGetReadonlyDraftMessageUids,
  makeGetReplyingTeammates,
  makeGetSendingActivities,
  makeGetSendingComments,
  makeGetSortedComposeDraftMessages,
  SendingActivitiesSelector,
} from '../../../../../store/panels/viewer/viewerPanelSelectors';
import {ViewerPanelConversationPageState} from '../../../../../store/panels/viewer/viewerPanelState';
import {makeGetIsPersonalizedDemoInboxActive} from '../../../../../store/stateless/guidedDemoSelectors';
import {ConversationTimeline, ConversationTimelineProps} from './conversationTimeline';

/*
 * Props.
 */

export type FetchMoreActivities = (
  direction: ScrollDirectionsEnum,
  referenceBoundary: ConversationTimelineBoundary,
) => void;

interface ConversationTimelineContainerPublicProps {
  panelId: ViewerPanelId;
  page: ViewerPanelConversationPageState;
  conversation: ConversationModel;
  cancelToken?: CancelToken;
}

/*
 * Selectors.
 */

function getPanelId(_state: AppState, props: ConversationTimelineContainerPublicProps) {
  return props.panelId;
}

function getPage(_state: AppState, props: ConversationTimelineContainerPublicProps) {
  return props.page;
}

function getConversation(_state: AppState, props: ConversationTimelineContainerPublicProps) {
  return props.conversation;
}

function getCellsContext(_state: AppState, props: ConversationTimelineContainerPublicProps) {
  return props.page.cellsContext;
}

function getSelectedInboxId(_state: AppState, props: ConversationTimelineContainerPublicProps) {
  return props.page.cellsContext.inboxId;
}

function makeGetHasChatPrecog() {
  return createSelector(
    [getConversation, getChatPrecogs],
    (conversation, chatPrecogs) => chatPrecogs[conversation.id] || false,
  );
}

function makeGetLastMessage(): OptionalMessageSelector<ConversationTimelineContainerPublicProps> {
  return createSelector(
    [getConversation, getMessages],
    (conversation, messages) => conversation.lastMessage && messages[conversation.lastMessage.uid],
  );
}

function makeGetLastActivityCommentAuthorId(
  getPageActivities: ActivitiesSelector<ConversationTimelineContainerPublicProps>,
) {
  return createSelector([getComments, getPageActivities], (comments, pageActivities) => {
    const lastActivity = pageActivities[pageActivities.length - 1];
    if (!lastActivity || lastActivity.visualType !== ActivityVisualTypesEnum.COMMENT) {
      return undefined;
    }

    const comment = comments[lastActivity.commentUid];
    return comment && comment.authorId;
  });
}

/** Find the canceled reminder for our user in this conversation. */
function makeGetCanceledReminder() {
  return createSelector([getConversation, getCellsContext], (conversation, cellsContext) =>
    findConversationsCanceledReminder(cellsContext, [conversation]),
  );
}
/**
 * Computes the set of messageUids that contain a new calendarEvent
 * in the timeline. This set can change when:
 * - new messages are loaded due to pagination.
 * - existing messages with unloaded content get loaded.
 */
type MessageUid = string;
function makeGetMessageUidsWithNewCalendarEvents(
  getPageActivities: ActivitiesSelector<ConversationTimelineContainerPublicProps>,
) {
  return createSelector([getPageActivities, getMessages], (activities, messages) => {
    const messageUidsWithNewCalendarEvents = new Set<MessageUid>();

    if (!activities) {
      return messageUidsWithNewCalendarEvents;
    }

    let lastCalendarEventUid: string | undefined;
    _(activities).forEach((activity) => {
      if (!activity || activity.visualType !== ActivityVisualTypesEnum.MESSAGE) {
        return;
      }

      const message = messages[activity.messageUid];
      if (!message || message.calendarEventUids.length === 0) {
        return;
      }

      const calendarEventUid = message.calendarEventUids[0];
      if (lastCalendarEventUid === calendarEventUid) {
        return;
      }

      lastCalendarEventUid = calendarEventUid;
      messageUidsWithNewCalendarEvents.add(message.uid);
    });

    return messageUidsWithNewCalendarEvents;
  });
}

function makeGetReplyChannelMessage(
  getLastMessage: OptionalMessageSelector<ConversationTimelineContainerPublicProps>,
  getSendingActivities: SendingActivitiesSelector<ConversationTimelineContainerPublicProps>,
) {
  return createSelector(
    [getHasListSendingMessagesInSentFolder, getLastMessage, getSendingActivities],
    (hasListSendingMessagesInSentFolder, lastMessage, sendingActivities) => {
      if (hasListSendingMessagesInSentFolder && lastMessage === undefined) {
        const sendingMessageActivities = sendingActivities.filter(
          (activity) => activity.message !== undefined,
        );
        return _.last(sendingMessageActivities)?.message ?? undefined;
      }

      return lastMessage;
    },
  );
}

/*
 * Component.
 */

interface ConversationTimelineContainerProps extends ConversationTimelineProps {
  userComposeDraftMessages: ReadonlyArray<DraftMessageModel>;
  replyChannel?: ChannelModel;
  cancelToken?: CancelToken;
}

interface ConversationTimelineContainerState {
  dontFocusComposer: boolean;
  lastUserComposeDraftMessages: ReadonlyArray<MessageModel>;
  lastConversationId?: number;
}

class ConversationTimelineContainerComponent extends Component<
  ConversationTimelineContainerProps,
  ConversationTimelineContainerState
> {
  constructor(props: ConversationTimelineContainerProps) {
    super(props);
    this.state = {
      dontFocusComposer: true,
      lastUserComposeDraftMessages: props.userComposeDraftMessages,
    };
  }

  /*
   * Lifecycle.
   */

  static getDerivedStateFromProps(
    props: Readonly<ConversationTimelineContainerProps>,
    state: Readonly<ConversationTimelineContainerState>,
  ) {
    const {userComposeDraftMessages, conversation, replyChannel} = props;
    if (userComposeDraftMessages === state.lastUserComposeDraftMessages) {
      return null;
    }

    const alwaysShowReplyComposer = Boolean(replyChannel && replyChannel.behaviors.alwaysShowReplyComposer);
    const hasNewDrafts = userComposeDraftMessages.length > state.lastUserComposeDraftMessages.length;

    return {
      dontFocusComposer: !alwaysShowReplyComposer && !hasNewDrafts,
      lastUserComposeDraftMessages: userComposeDraftMessages,
      lastConversationId: conversation.id,
    };
  }

  /*
   * Render.
   */

  render() {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <ConversationTimeline {...this.props} dontFocusComposer={this.state.dontFocusComposer} />;
  }
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationTimelineContainerPublicProps>()(() => {
  const getPageActivities = makeGetPageActivities(getPage);

  const getSendingComments = makeGetSendingComments(getConversation);
  const getDraftMessages = makeGetLoadedDraftMessages(getConversation);
  const getDraftSeedAuthors = wrapInShallowSelector(makeGetDraftSeedAuthors(getConversation));
  const getSendingActivities = wrapInShallowSelector(
    makeGetSendingActivities(getSendingComments, getDraftMessages),
  );

  const getPrecogs = wrapInShallowSelector(makeGetPrecogs(getConversation));
  const getCommentingTeammates = wrapInShallowSelector(makeGetCommentingTeammates(getPrecogs));
  const getHasChatPrecog = wrapInShallowSelector(makeGetHasChatPrecog());
  const getReplyingTeammates = wrapInShallowSelector(makeGetReplyingTeammates(getPrecogs));

  const getSortedComposeDraftMessages = makeGetSortedComposeDraftMessages(getDraftMessages);
  const getUserComposeDraftMessages = makeGetUserDraftComposeMessages(getSortedComposeDraftMessages);
  const getReadonlyDraftMessageUids = wrapInArrayToSetSelector(
    wrapInShallowSelector(makeGetReadonlyDraftMessageUids(getUserComposeDraftMessages, getPanelId)),
  );

  const getLastMessage = makeGetLastMessage();
  const getLastActivityCommentAuthorId = makeGetLastActivityCommentAuthorId(getPageActivities);

  const getReplyChannelMessage = makeGetReplyChannelMessage(getLastMessage, getSendingActivities);

  const getReplyChannel = makeGetReplyChannel({getMessage: getReplyChannelMessage, getSelectedInboxId});

  const getIsSpam = makeGetConversationIsSpam(getConversation);
  const getFetchedAt = makeGetFetchedAt(getConversation);

  const getCanceledReminder = makeGetCanceledReminder();

  const getIsPersonalizedDemoInboxActive = makeGetIsPersonalizedDemoInboxActive(getConversation);

  const getMessageUidsWithNewCalendarEvents = makeGetMessageUidsWithNewCalendarEvents(getPageActivities);

  return {
    userId: getUserId,
    findTeammate: getFindTeammate,
    inboxes: getInboxes,
    userAlias: getUserAlias,
    activities: getPageActivities,
    sendingActivities: getSendingActivities,
    commentingTeammates: getCommentingTeammates,
    hasChatPrecog: getHasChatPrecog,
    replyingTeammates: getReplyingTeammates,
    draftSeedAuthors: getDraftSeedAuthors,
    composeDraftMessages: getSortedComposeDraftMessages,
    userComposeDraftMessages: getUserComposeDraftMessages,
    readonlyDraftMessageUids: getReadonlyDraftMessageUids,
    lastActivityCommentAuthorId: getLastActivityCommentAuthorId,
    replyChannel: getReplyChannel,
    isSpam: getIsSpam,
    canceledReminder: getCanceledReminder,
    fetchedAt: getFetchedAt,
    hasRecentCommunicationsBanner: getHasRecentCommunicationsBanner,
    hasRecentCommunicationsComposerBanner: getHasRecentCommunicationsComposerBanner,
    isPersonalizedDemoInboxActive: getIsPersonalizedDemoInboxActive,
    messageUidsWithNewCalendarEvents: getMessageUidsWithNewCalendarEvents,
    hasAutoShareDrafts: getHasAutoShareDrafts,
  };
});

const mapDispatchToProps = (dispatch: Dispatch, props: ConversationTimelineContainerPublicProps) => ({
  fetchMore: (direction: ScrollDirectionsEnum) => {
    const {panelId, page} = props;

    // Find the activity at the edge that we'll use as reference for the next batch.
    const isTop = direction === ScrollDirectionsEnum.TOP;
    const referenceBoundary = isTop ? page.topBoundary : page.bottomBoundary;
    if (!referenceBoundary) {
      return;
    }

    dispatch(fetchMoreConversationAction(panelId, page, direction, referenceBoundary));
  },
  deleteEmptyDrafts: () => {
    dispatch(
      deleteEmptyDraftsAction({
        conversationId: props.conversation.id,
      }),
    );
  },
});

export const ConversationTimelineContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ConversationTimelineContainerComponent);
